import type { ComputedRef } from 'vue'

export type CalculatorDomain = 'steel' | 'concrete' | 'specialized'

export type CalculatorStage = 'live' | 'beta' | 'backend' | 'planned'

export interface CalculatorMeta {
  slug: string
  name: string
  shortDescription: string
  domain: CalculatorDomain
  stage: CalculatorStage
  route?: string
  code?: string
  tags?: string[]
  pro?: boolean
  estimatedRelease?: string
}

export interface DirectoryGroup {
  domain: CalculatorDomain
  title: string
  description: string
  calculators: CalculatorMeta[]
}

export const calculatorDomainCopy: Record<CalculatorDomain, { title: string; description: string }> = {
  steel: {
    title: 'Steel Design (IS 800)',
    description: 'Gantry girders, base plates, compression members, and connection design per IS 800/IS 801.'
  },
  concrete: {
    title: 'Concrete Design (IS 456)',
    description: 'Limit state concrete design calculators covering flexure, shear, footings, and retaining walls.'
  },
  specialized: {
    title: 'Specialized Tools',
    description: 'Focused utilities that support IS code workflows: lookups, combinations, and verification aids.'
  }
}

export const calculatorsMeta: CalculatorMeta[] = [
  // Steel Design Suite
  {
    slug: 'base_plate',
    name: 'Base Plate Design',
    shortDescription: 'Anchor bolt, grout thickness, and bearing checks for steel column base plates.',
    domain: 'steel',
    stage: 'beta',
    route: '/base-plate',
    code: 'IS 800:2007',
  },
  {
    slug: 'steel_column',
    name: 'Steel Column Design',
    shortDescription: 'Axial capacity, slenderness, and buckling checks for steel columns.',
    domain: 'steel',
    stage: 'live',
    route: '/steel-column',
    code: 'IS 800:2007'
  },
  // {
  //   slug: 'connection_design',
  //   name: 'Connection Design',
  //   shortDescription: 'Bolted and welded steel connection design with limit state verifications.',
  //   domain: 'steel',
  //   stage: 'backend',
  //   code: 'IS 800:2007'
  // },
  {
    slug: 'purlin_design',
    name: 'Purlin Design',
    shortDescription: 'Cold-formed steel purlin optimisation with web crippling and biaxial bending checks.',
    domain: 'steel',
    stage: 'live',
    route: '/calculators/steel/purlin-design',
    code: 'IS 801:1975'
  },
  {
    slug: 'gantry_girder',
    name: 'Gantry Girder',
    shortDescription: 'Crane surge, fatigue, and deflection checks for high-duty gantry girders.',
    domain: 'steel',
    stage: 'live',
    route: '/calculators/steel/gantry-girder',
    code: 'IS 800:2007'
  },
  // Concrete Design Suite
  {
    slug: 'conc_beam_design',
    name: 'Concrete Beam Design',
    shortDescription: 'Flexural, shear, and serviceability design of reinforced concrete beams.',
    domain: 'concrete',
    stage: 'beta',
    route: '/calculators/concrete/conc-beam-design',
    code: 'IS 456:2000'
  },
  {
    slug: 'column_design',
    name: 'Column Design',
    shortDescription: 'Axial load, biaxial bending, and slenderness checks for RC columns.',
    domain: 'concrete',
    stage: 'beta',
    route: '/calculators/concrete/column-design',
    code: 'IS 456:2000'
  },
  {
    slug: 'crack_width',
    name: 'Crack Width',
    shortDescription: 'Serviceability crack width calculations for reinforced concrete members.',
    domain: 'concrete',
    stage: 'live',
    route: '/calculators/concrete/crack-width',
    code: 'IS 456:2000'
  },
  {
    slug: 'slab_design',
    name: 'Slab Design',
    shortDescription: 'One-way and two-way beam slab design using coefficient method.',
    domain: 'concrete',
    stage: 'live',
    route: '/calculators/concrete/slab-design',
    code: 'IS 456:2000'
  },
  {
    slug: 'footing_design',
    name: 'Isolated Footing Design',
    shortDescription: 'Design isolated column footings with checks for bearing, shear, and stability per IS 456:2000.',
    domain: 'concrete',
    stage: 'beta',
    route: '/calculators/concrete/footing-design',
    code: 'IS 456:2000'
  },

  {
    slug: 'retaining_wall',
    name: 'Retaining Wall Design',
    shortDescription: 'Earth pressure, overturning, sliding, and bearing checks for cantilever and counterfort retaining walls.',
    domain: 'concrete',
    stage: 'live',
    route: '/calculators/concrete/retaining-wall',
    code: 'IS 456:2000'
  },

  // Tools Suite
  {
    slug: 'is-code-lookup',
    name: 'IS Code Table Lookup',
    shortDescription: 'Interpolate values directly from standard IS code design tables (IS 800, IS 456).',
    domain: 'specialized',
    stage: 'live',
    route: '/calculators/specialized/lookup',
    code: 'IS 800 / IS 456'
  },

  // Specialized Tools
  {
    slug: 'staad_mto',
    name: 'STAAD Procurement-Ready MTO',
    shortDescription: 'Generate structural steel BOQ with rolled section and plate-wise built-up take-off from .std files.',
    domain: 'specialized',
    stage: 'live',
    route: '/calculators/specialized/staad-plate-section-mto',
    code: 'STAAD .std'
  },
  {
    slug: 'load_combination_generator',
    name: 'Load Combination Generator',
    shortDescription: 'IS 800 / IS 456 / IS 1893 compliant load combination builder for gravity and seismic load cases and envelopes.',
    domain: 'specialized',
    stage: 'live',
    route: '/calculators/specialized/load-combinations'
  },

  // Specialized Optimization
  {
    slug: 'peb_optimizer',
    name: 'PEB Optimizer',
    shortDescription: 'Iterative optimization of pre-engineered building rafter sections via STAAD.Pro automation.',
    domain: 'specialized',
    stage: 'beta',
    route: '/calculators/specialized/peb-optimizer',
    code: 'IS 800:2007'
  }
]


export interface CategoryMeta {
  icon: string
  standardBadge: string
  featuredSlug: string
  quickLinks: string[]
}

export const domainCategoryMeta: Record<CalculatorDomain, CategoryMeta> = {
  steel: {
    icon: 'mdi-transmission-tower',
    standardBadge: 'IS 800:2007',
    featuredSlug: 'steel_column',
    quickLinks: ['steel_column', 'base_plate', 'gantry_girder']
  },
  concrete: {
    icon: 'mdi-domain',
    standardBadge: 'IS 456:2000',
    featuredSlug: 'slab_design',
    quickLinks: ['slab_design', 'conc_beam_design', 'footing_design']
  },
  specialized: {
    icon: 'mdi-book-open-variant',
    standardBadge: 'IS 800 / IS 456 / IS 1893',
    featuredSlug: 'staad_mto',
    quickLinks: ['staad_mto', 'is-code-lookup', 'load_combination_generator']
  }
}

export type FilterState = {
  search: string
  domains: CalculatorDomain[]
  stages: CalculatorStage[]
}

export type FilterStateRef = ComputedRef<FilterState>
